import OverlayManager from "@stardust/overlay-manager";
import { createFocusTrap } from "@stardust/focus-trap";
import { lockScroll, unlockScroll } from "@stardust/scroll-lock";

/**
 * BaseDrawer - Base class for slide-in drawer components (modals, sidebars, etc.)
 *
 * Provides complete drawer lifecycle management including:
 * - Animated open/close with CSS class transitions
 * - Focus trap inside drawer (keyboard nav, ESC key)
 * - Shared overlay management (blocks background interactions)
 * - Body scroll lock while drawer is open
 * - Proper cleanup on destroy
 *
 * @class
 * @example
 * const drawer = new BaseDrawer(element, { animationDuration: 300 });
 * drawer.open();
 * drawer.close();
 * drawer.destroy();
 */
export default class BaseDrawer {
  /**
   * Initialize a new drawer.
   *
   * @param {HTMLElement} element - The drawer element to control
   * @param {Object} options - Configuration options
   * @param {string} options.openClass - CSS classes removed when opening (default: 'opacity-0 translate-x-full')
   * @param {string} options.closeClass - CSS classes added when closing (default: same as openClass)
   * @param {number} options.animationDuration - Duration of animations in ms (default: 300)
   * @param {number|string} options.overlayZIndex - CSS z-index for overlay (default: 9998)
   * @param {boolean} options.lockScroll - Lock body scroll while drawer is open (default: true)
   */
  constructor(element, options = {}) {
    this.element = element;
    this.isOpen = false;
    this._scrollLocked = false;

    // Options with defaults
    this.options = {
      openClass: options.openClass || 'opacity-0 translate-x-full',
      closeClass: options.closeClass || options.openClass || 'opacity-0 translate-x-full',
      animationDuration: options.animationDuration || 300,
      // Matches overlay-manager.js's focus overlay (z-9998). The sticky header is a stacking
      // context — at 9999 the overlay wins the z-tie by DOM order and swallows drawer clicks.
      overlayZIndex: options.overlayZIndex || 9998,
      lockScroll: options.lockScroll !== false,
      ...options
    };

    // Focus trap
    this.focusTrap = createFocusTrap(element, {
      escapeDeactivates: true,
      clickOutsideDeactivates: false,
    });

    // Overlay references
    this._overlayOwner = null;
    this._overlayHandler = null;

    // Bound handlers for cleanup
    this._boundClose = this.close.bind(this);
    this._boundOpen = this.open.bind(this);

    // All drawers start closed — block keyboard/AT access to their children
    this.element.setAttribute('inert', '');

    // ESC key handler - only active when drawer is open
    this._handleEsc = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };
    // Use capture phase for faster response
    document.addEventListener('keydown', this._handleEsc, { capture: true });
  }

  /**
   * Open the drawer with animation. Activates focus trap and shows overlay.
   * If drawer is already open, this is a no-op.
   *
   * Subclasses can define an `onOpen()` method to hook into the open lifecycle.
   *
   * @returns {void}
   */
  open() {
    if (this.isOpen) return;

    this.isOpen = true;

    // Restore keyboard/AT access before activating focus trap
    this.element.removeAttribute('inert');

    // Enable pointer events — explicit auto, not just removing none: drawers inside <header>
    // would inherit pointer-events:none from the hideSearchBar animation if we only removed the class.
    this.element.classList.remove("pointer-events-none");
    this.element.classList.add("pointer-events-auto");
    this.element.setAttribute('data-drawer-open', '');
    this.element.style.willChange = 'transform, opacity';

    // Activate focus trap — the internal querySelectorAll is now deferred to rAF
    // so it no longer affects the click handler's processing duration
    try {
      this.focusTrap.activate();
    } catch (e) {
      console.warn("[BaseDrawer] Focus trap activation failed:", e);
    }

    // Defer overlay setup and scroll lock to rAF to keep main thread responsive and allow the open animation to start immediately
    const openClasses = this.options.openClass.split(' ').filter(Boolean);
    requestAnimationFrame(() => {
      if (!this.isOpen) return; // closed before frame fired

      try {
        this._overlayOwner = OverlayManager.show({
          zIndex: this.options.overlayZIndex,
          before: this.element
        });
      } catch (e) {
        try { this._overlayOwner = OverlayManager.show(); } catch (e2) { /* ignore */ }
      }
      if (this._overlayHandler) {
        try { OverlayManager.remove(this._overlayHandler); } catch (e) { /* ignore */ }
      }
      this._overlayHandler = this._boundClose;
      try { OverlayManager.add(this._overlayHandler); } catch (e) { /* ignore */ }

      if (this.options.lockScroll) {
        lockScroll();
        this._scrollLocked = true;
      }

      requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.element.classList.remove(...openClasses);
        setTimeout(() => { this.element.style.willChange = ''; }, this.options.animationDuration);
      });
    });

    // Hook for subclasses
    this.onOpen?.();
  }

  /**
   * Close the drawer with animation. Deactivates focus trap, hides overlay, and restores scroll lock.
   * If drawer is already closed, this is a no-op.
   *
   * Subclasses can define an `onClose()` method to hook into the close lifecycle.
   *
   * @returns {void}
   */
  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Block keyboard/AT access immediately — don't wait for animation to finish
    this.element.setAttribute('inert', '');

    // Unlock scroll immediately and clear the flag so a re-open race can't double-unlock
    if (this.options.lockScroll && this._scrollLocked) {
      unlockScroll();
      this._scrollLocked = false;
    }

    // Deactivate focus trap immediately for fast INP
    try {
      this.focusTrap.deactivate();
    } catch (e) {
      console.warn("[BaseDrawer] Focus trap deactivation failed:", e);
    }

    // Add will-change for smooth close animation
    this.element.style.willChange = 'transform, opacity';

    // Add close animation classes
    const closeClasses = this.options.closeClass.split(' ').filter(Boolean);
    this.element.classList.add(...closeClasses);

    // Remove overlay (non-blocking)
    try { OverlayManager.remove(this._overlayHandler); } catch (e) { /* ignore */ }
    try { OverlayManager.hide(this._overlayOwner); } catch (e) { /* ignore */ }
    this._overlayOwner = null;

    // Hook for subclasses
    this.onClose?.();

    // Use rAF for cleanup after animation - keeps main thread responsive
    const cleanup = () => {
      // Guard against re-open racing the animation timeout
      if (this.isOpen) return;
      this.element.classList.remove("pointer-events-auto");
      this.element.classList.add("pointer-events-none");
      this.element.removeAttribute('data-drawer-open');
      this.element.style.willChange = '';
    };

    // Schedule cleanup after animation, but use rAF to avoid blocking
    setTimeout(() => requestAnimationFrame(cleanup), this.options.animationDuration);

    // Mark a short-lived global flag to prevent immediate re-open races
    window.__modalJustClosedAt = Date.now();
    setTimeout(() => { window.__modalJustClosedAt = 0; }, 250);
  }

  /**
   * Toggle open/close state. Opens if currently closed, closes if currently open.
   *
   * @returns {void}
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Clean up all event listeners, focus trap, and drawer state.
   * Closes the drawer if currently open before cleanup.
   * After destroy, the BaseDrawer instance should not be reused.
   *
   * @returns {void}
   */
  destroy() {
    if (this.isOpen) {
      this.close();
    }

    document.removeEventListener('keydown', this._handleEsc, { capture: true });

    try {
      this.focusTrap.deactivate();
    } catch (e) { }
  }
}
